/**
 * Утилиты фона карточки профиля (картинка + настройки отображения).
 * Контракт бэкенда (docs: profile background): ключ `background` в объекте profile,
 * при отсутствии фона — null целиком. Настройки: fit / position_x / position_y /
 * overlay / blur. URL приходит готовый, кэш-бастинга не требует.
 */

// Дефолты настроек — зеркалят значения бэкенда
export const BG_DEFAULTS = {
  fit: 'cover',
  position_x: 50,
  position_y: 50,
  overlay: 30,
  blur: 0
}

export const BG_FITS = ['cover', 'contain', 'stretch', 'repeat']

// Лимиты файла из контракта: JPG/PNG, ≤1 МБ, обе стороны ≤500 px
export const BG_MAX_SIDE = 500
export const BG_MAX_BYTES = 1024 * 1024

const clampInt = (value, min, max, fallback) => {
  const v = Math.round(Number(value))
  return Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback
}

/** Нормализованные настройки фона: неизвестные значения → дефолты */
export const fromBackground = (bg) => ({
  fit: BG_FITS.includes(bg?.fit) ? bg.fit : BG_DEFAULTS.fit,
  position_x: clampInt(bg?.position_x, 0, 100, BG_DEFAULTS.position_x),
  position_y: clampInt(bg?.position_y, 0, 100, BG_DEFAULTS.position_y),
  overlay: clampInt(bg?.overlay, 0, 100, BG_DEFAULTS.overlay),
  blur: clampInt(bg?.blur, 0, 20, BG_DEFAULTS.blur)
})

/**
 * Diff для автосохранения настроек: только изменённые ключи.
 * Возвращает null, если отправлять нечего.
 */
export const buildBackgroundPayload = (draft, saved) => {
  const payload = {}
  Object.keys(BG_DEFAULTS).forEach((key) => {
    if (draft[key] !== saved[key]) payload[key] = draft[key]
  })
  return Object.keys(payload).length > 0 ? payload : null
}

/**
 * Стиль слоя картинки (absolute): background-size/position/repeat + blur.
 * Размытие «съедает» края — слой расширяется за карточку (overflow-hidden режет лишнее).
 */
export const backgroundImageStyle = (bg) => {
  const settings = fromBackground(bg)
  const inset = settings.blur > 0 ? -settings.blur * 2 : 0
  return {
    top: inset,
    right: inset,
    bottom: inset,
    left: inset,
    backgroundImage: `url("${bg?.url}")`,
    backgroundSize: settings.fit === 'stretch' ? '100% 100%' : settings.fit,
    backgroundPosition: `${settings.position_x}% ${settings.position_y}%`,
    backgroundRepeat: settings.fit === 'repeat' ? 'repeat' : 'no-repeat',
    filter: settings.blur > 0 ? `blur(${settings.blur}px)` : undefined
  }
}

/**
 * Стиль слоя затемнения (absolute inset-0): тёмный scrim поверх картинки —
 * текст карточки (ник, лайки, статус) остаётся читаемым на светлых картинках.
 */
export const backgroundOverlayStyle = (bg) => ({
  backgroundColor: `rgba(10, 10, 15, ${clampInt(bg?.overlay, 0, 100, BG_DEFAULTS.overlay) / 100})`
})

/**
 * Подготовка файла к загрузке: если стороны больше 500 px — уменьшение через canvas
 * (браузер при декодировании применяет EXIF-ориентацию, так что поворот сохраняется).
 * PNG перекодируется в PNG (альфа жива), остальное — в JPEG.
 * Файл в пределах лимитов возвращается как есть.
 * @throws {{code: string}} invalid_image — не декодируется; file_too_large — не влезает в 1 МБ
 */
export const prepareBackgroundFile = (file) => new Promise((resolve, reject) => {
  const isPng = file.type === 'image/png'
  const finish = (blob, name) => {
    if (blob.size > BG_MAX_BYTES) {
      reject({ code: 'file_too_large' })
      return
    }
    resolve({ blob, name })
  }

  const url = URL.createObjectURL(file)
  const img = new Image()
  img.onload = () => {
    URL.revokeObjectURL(url)

    const needsResize = img.naturalWidth > BG_MAX_SIDE || img.naturalHeight > BG_MAX_SIDE
    if (!needsResize) {
      finish(file, file.name)
      return
    }

    const scale = Math.min(BG_MAX_SIDE / img.naturalWidth, BG_MAX_SIDE / img.naturalHeight)
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject({ code: 'invalid_image' })
          return
        }
        // canvas не сохраняет имя файла — задаём сами по фактическому формату
        finish(blob, isPng ? 'background.png' : 'background.jpg')
      },
      isPng ? 'image/png' : 'image/jpeg',
      isPng ? undefined : 0.92
    )
  }
  img.onerror = () => {
    URL.revokeObjectURL(url)
    reject({ code: 'invalid_image' })
  }
  img.src = url
})
